// @ts-check

(() => {
  const rootElement = document.querySelector('[data-post-launch-popup]');
  if (!(rootElement instanceof HTMLElement)) return;

  /** @type {HTMLElement} */
  const root = rootElement;

  const klaviyoListId = (root.dataset.klaviyoListId || 'Y7XSDp').trim();
  const storageKey = root.dataset.storageKey || 'chaser_post_launch_popup_closed';
  const successMessage = root.dataset.successMessage || "You're on the list.";
  const duplicateMessage = root.dataset.duplicateMessage || 'This email is already on the list.';
  const invalidEmailMessage = root.dataset.invalidEmailMessage || 'Please enter a valid email address.';
  const unavailableMessage =
    root.dataset.unavailableMessage || 'Signup is temporarily unavailable. Please try again later.';
  const loadingLabel = root.dataset.loadingLabel || 'Joining…';

  const form = root.querySelector('[data-post-launch-form], .post-launch-popup__form');
  const formWrap = root.querySelector('[data-post-launch-form-wrap]');
  const emailInput = root.querySelector('[data-post-launch-email]');
  const submitButton = root.querySelector('[data-post-launch-submit]');
  const errorEl = root.querySelector('[data-post-launch-error]');
  const successEl = root.querySelector('[data-post-launch-success]');
  const successTextEl = root.querySelector('.post-launch-popup__success-text');
  const closeButton = root.querySelector('[data-post-launch-close]');
  const backdrop = root.querySelector('[data-post-launch-backdrop]');

  /**
   * @param {string} value
   * @returns {boolean}
   */
  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const isClosed = () => {
    try {
      return window.localStorage.getItem(storageKey) === 'true';
    } catch (_) {
      return false;
    }
  };

  const setClosed = () => {
    try {
      window.localStorage.setItem(storageKey, 'true');
    } catch (_) {
      // Ignore storage failures.
    }
  };

  const openPopup = () => {
    root.hidden = false;
    root.removeAttribute('hidden');
    document.documentElement.classList.add('post-launch-popup-open');
  };

  const closePopup = () => {
    root.hidden = true;
    root.setAttribute('hidden', '');
    document.documentElement.classList.remove('post-launch-popup-open');
    setClosed();
  };

  /**
   * @param {string} message
   */
  const showError = (message) => {
    if (!(errorEl instanceof HTMLElement)) return;
    errorEl.textContent = message;
    errorEl.hidden = false;
    errorEl.removeAttribute('hidden');

    if (emailInput instanceof HTMLInputElement) {
      emailInput.classList.add('post-launch-popup__input--invalid');
      emailInput.setAttribute('aria-invalid', 'true');
      emailInput.focus();
    }
  };

  const clearError = () => {
    if (!(errorEl instanceof HTMLElement)) return;
    errorEl.textContent = '';
    errorEl.hidden = true;

    if (emailInput instanceof HTMLInputElement) {
      emailInput.classList.remove('post-launch-popup__input--invalid');
      emailInput.removeAttribute('aria-invalid');
    }
  };

  /**
   * @param {boolean} isLoading
   */
  const setLoading = (isLoading) => {
    if (!(submitButton instanceof HTMLButtonElement)) return;

    if (isLoading) {
      submitButton.setAttribute('aria-busy', 'true');
      submitButton.setAttribute('aria-label', loadingLabel);
      submitButton.disabled = true;
      return;
    }

    submitButton.removeAttribute('aria-busy');
    submitButton.disabled = false;
  };

  const showSuccess = () => {
    if (formWrap instanceof HTMLElement) {
      formWrap.hidden = true;
    }

    if (successEl instanceof HTMLElement) {
      successEl.hidden = false;
      successEl.removeAttribute('hidden');
    }

    if (successTextEl instanceof HTMLElement) {
      successTextEl.textContent = successMessage;
    }
  };

  /**
   * @param {unknown} value
   * @returns {boolean}
   */
  const isTruthyFlag = (value) => value === true || value === 'true' || value === 1 || value === '1';

  /**
   * Subscribe to the shared newsletter Email List (same as footer newsletter sync).
   * Does not apply early-access tags.
   *
   * @param {string} email
   * @returns {Promise<'duplicate' | 'available' | 'unavailable'>}
   */
  const subscribeToNewsletterList = async (email) => {
    try {
      const response = await fetch('https://manage.kmail-lists.com/ajax/subscriptions/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({ g: klaviyoListId, email }),
      });

      if (!response.ok) return 'unavailable';

      const json = await response.json();
      if (!json?.success) return 'unavailable';

      const data = json.data || {};
      if (
        isTruthyFlag(data.is_subscribed) ||
        isTruthyFlag(data.already_subscribed) ||
        isTruthyFlag(data.is_already_subscribed)
      ) {
        return 'duplicate';
      }

      return 'available';
    } catch (_) {
      return 'unavailable';
    }
  };

  /**
   * Mirror the footer newsletter signup in Shopify (tag: newsletter only).
   *
   * @param {HTMLFormElement} customerForm
   * @returns {Promise<void>}
   */
  const registerShopifyNewsletter = async (customerForm) => {
    const action = customerForm.getAttribute('action') || `${window.Shopify?.routes?.root || '/'}contact`;
    const formData = new FormData(customerForm);

    try {
      await fetch(action, {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'text/html',
        },
        credentials: 'same-origin',
      });
    } catch (_) {
      // Klaviyo list membership is the primary newsletter destination.
    }
  };

  if (isClosed()) {
    closePopup();
    return;
  }

  openPopup();

  closeButton?.addEventListener('click', closePopup);
  backdrop?.addEventListener('click', closePopup);
  emailInput?.addEventListener('input', clearError);

  form?.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (!(emailInput instanceof HTMLInputElement)) return;
    if (!(form instanceof HTMLFormElement)) return;

    const email = emailInput.value.trim().toLowerCase();
    emailInput.value = email;
    clearError();

    if (!isValidEmail(email)) {
      showError(invalidEmailMessage);
      return;
    }

    if (!klaviyoListId) {
      showError(unavailableMessage);
      return;
    }

    setLoading(true);
    try {
      const status = await subscribeToNewsletterList(email);

      if (status === 'duplicate') {
        showError(duplicateMessage);
        return;
      }

      if (status === 'unavailable') {
        showError(unavailableMessage);
        return;
      }

      await registerShopifyNewsletter(form);
      showSuccess();
      setClosed();
      window.setTimeout(closePopup, 1200);
    } finally {
      setLoading(false);
    }
  });
})();
